import { useEffect, useRef, useState } from 'react';
import * as cornerstone from '@cornerstonejs/core';
import { imageLoadPoolManager, Enums } from '@cornerstonejs/core';
import { Dicom } from '@/models/dicom';
import { useGetMediaQuery } from '@/state/slices/media';
import { Media } from '@/models/media';
import { videoBufferPool } from '../utils/videoBufferPool';

interface UseAdjacentDicomPreloaderOptions {
  studyUuid: string;
  dicoms: Dicom[];
  currentGroupIndex: number;
  viewportCount: number;
  preloadRange?: number;
  enabled?: boolean;
}

/**
 * Collect DICOMs from groups adjacent to the current one.
 * Returns Dicom objects (not just UUIDs) so callers can inspect numberOfFrames.
 */
function getAdjacentDicoms(
  dicoms: Dicom[],
  currentGroupIndex: number,
  viewportCount: number,
  range: number
): Dicom[] {
  const result: Dicom[] = [];

  for (let offset = 1; offset <= range; offset += 1) {
    // Previous group
    const prevStart = (currentGroupIndex - offset) * viewportCount;
    if (prevStart >= 0) {
      for (let i = 0; i < viewportCount; i += 1) {
        const idx = prevStart + i;
        if (idx < dicoms.length) result.push(dicoms[idx]);
      }
    }

    // Next group
    const nextStart = (currentGroupIndex + offset) * viewportCount;
    for (let i = 0; i < viewportCount; i += 1) {
      const idx = nextStart + i;
      if (idx < dicoms.length) result.push(dicoms[idx]);
    }
  }

  return result;
}

/** Find the media item for a DICOM from the shared media cache. */
function findMedia(
  results: Media[],
  dicomUuid: string,
  isVideo: boolean
): Media | undefined {
  return results.find(
    (m) =>
      m.dicomUuid === dicomUuid &&
      m.file &&
      m.type === (isVideo ? 'VIDEO' : 'IMAGE')
  );
}

/**
 * Preloads adjacent DICOM groups for instant transitions.
 *
 * Strategy:
 * - VIDEO DICOMs: Creates offscreen `<video>` elements via `videoBufferPool`
 *   so the browser has decoded bytes ready. When Cornerstone's `setVideoURL()`
 *   later requests the same URL, the browser serves from HTTP cache instantly.
 * - IMAGE DICOMs: Loads into Cornerstone's internal cache via
 *   `loadAndCacheImage()` through the prefetch pool (16 concurrent).
 * - No delay — preloading starts immediately. Current viewport has priority
 *   via the Interaction request type.
 * - Dynamic range: expands to ±2 groups when user is scrolling quickly (<1s
 *   between group changes), otherwise ±1.
 * - Checks actual cache state (not permanent flags) so evicted items get
 *   re-preloaded when they become adjacent again.
 */
export function useAdjacentDicomPreloader({
  studyUuid,
  dicoms,
  currentGroupIndex,
  viewportCount,
  preloadRange = 1,
  enabled = true,
}: UseAdjacentDicomPreloaderOptions) {
  const lastGroupChangeRef = useRef<number>(0);

  // Re-trigger preloading when the page regains visibility.
  // After idle, the browser may GC decoded video bytes — the readyState
  // check in videoBufferPool.isReady() will detect stale entries and
  // the preloader will re-buffer them.
  const [visibilityTick, setVisibilityTick] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setVisibilityTick((t) => t + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled]);

  // Share the same RTK Query cache as ViewerPane — no duplicate API calls
  const { data: mediaData } = useGetMediaQuery(
    { study_uuid: studyUuid, paginate: false },
    { skip: !studyUuid || !enabled }
  );

  useEffect(() => {
    if (!enabled || dicoms.length === 0 || !mediaData?.results) return;

    // Dynamic range: expand to ±2 when the user switched groups less than 1s ago
    const now = Date.now();
    const timeSinceLastChange = now - lastGroupChangeRef.current;
    lastGroupChangeRef.current = now;
    const effectiveRange =
      timeSinceLastChange > 0 && timeSinceLastChange < 1000
        ? Math.max(preloadRange, 2)
        : preloadRange;

    // Keep current group's videos in the buffer pool (updates LRU timestamp).
    // This ensures the currently viewed DICOM stays cached when navigating away,
    // so going back is instant.
    const currentStart = currentGroupIndex * viewportCount;
    for (let i = 0; i < viewportCount; i += 1) {
      const idx = currentStart + i;
      if (idx < dicoms.length) {
        const d = dicoms[idx];
        if (d.numberOfFrames > 1) {
          const m = findMedia(mediaData.results, d.uuid, true);
          if (m?.file) videoBufferPool.preload(d.uuid, m.file);
        }
      }
    }

    // Preload adjacent groups — check actual cache state so evicted items
    // get re-preloaded (no permanent "already done" flags).
    const adjacentDicoms = getAdjacentDicoms(
      dicoms,
      currentGroupIndex,
      viewportCount,
      effectiveRange
    );

    adjacentDicoms.forEach((dicom) => {
      const isVideo = dicom.numberOfFrames > 1;

      if (isVideo) {
        // Skip only if the video is actually still buffered in the pool
        if (videoBufferPool.isReady(dicom.uuid)) return;

        const media = findMedia(mediaData.results, dicom.uuid, true);
        if (media?.file) {
          videoBufferPool.preload(dicom.uuid, media.file);
        }
      } else {
        const media = findMedia(mediaData.results, dicom.uuid, false);
        if (!media?.file) return;

        // Skip only if the image is actually still in Cornerstone's cache
        const imageId = `web:${media.file}`;
        if (!cornerstone.cache.isLoaded(imageId)) {
          imageLoadPoolManager.addRequest(
            () => cornerstone.imageLoader.loadAndCacheImage(imageId),
            Enums.RequestType.Prefetch,
            { imageId },
            5
          );
        }
      }
    });
  }, [
    dicoms,
    currentGroupIndex,
    viewportCount,
    preloadRange,
    enabled,
    mediaData,
    visibilityTick,
  ]);
}
